import { z } from 'zod';
/**
 * Email block vocabulary — the SAFETY BOUNDARY for the rich email/content
 * system. Agents and clients compose messages from these STRUCTURED BLOCKS,
 * NEVER raw HTML: every block is plain data, and the server-side renderer
 * (apps/api renderBlocksToEmail) is the only thing that turns blocks into
 * HTML, escaping all text and force-appending the CASL unsubscribe footer.
 *
 * This zod schema IS that boundary:
 *   - `z.discriminatedUnion('type', …)` REJECTS any unknown block type.
 *   - every member is `.strict()`, so a stray `html` / `script` / any field
 *     not declared below is REJECTED (no raw-HTML smuggling).
 *   - every url/href must be http(s) (no `javascript:` / `data:` schemes).
 *
 * There is deliberately NO `html` field anywhere in this file.
 */
/** http(s) URL only — blocks a `javascript:` / `data:` href reaching a render. */
const httpUrl = z
    .string()
    .trim()
    .url()
    .refine((u) => /^https?:\/\//i.test(u), { message: 'URL must be http(s)' });
const text = (max) => z.string().trim().min(1).max(max);
// ── Blocks (each plain data, `.strict()` = no unknown fields) ───────────────
export const HeadingBlockSchema = z.object({
    type: z.literal('heading'),
    text: text(200),
    /** 1 = section title, 2 = default, 3 = sub-heading. */
    level: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
}).strict();
export const ParagraphBlockSchema = z.object({
    type: z.literal('paragraph'),
    /** Rendered with a SAFE inline subset only (bold / italic / http links). */
    text: text(5_000),
}).strict();
const ImageItemSchema = z.object({
    url: httpUrl,
    alt: z.string().trim().max(300),
    href: httpUrl.optional(),
}).strict();
export const ImageBlockSchema = z.object({
    type: z.literal('image'),
    url: httpUrl,
    alt: z.string().trim().max(300),
    href: httpUrl.optional(),
}).strict();
export const ImageRowBlockSchema = z.object({
    type: z.literal('image_row'),
    images: z.array(ImageItemSchema).min(2).max(3),
}).strict();
export const ButtonBlockSchema = z.object({
    type: z.literal('button'),
    label: text(60),
    href: httpUrl,
}).strict();
/** A poster image that LINKS OUT to a hosted video/page. Email clients cannot
 *  embed a playable video, so a newsletter "video" is always a linked poster. */
export const VideoPosterBlockSchema = z.object({
    type: z.literal('video_poster'),
    posterUrl: httpUrl,
    href: httpUrl,
    alt: z.string().trim().max(300),
}).strict();
export const DividerBlockSchema = z.object({
    type: z.literal('divider'),
}).strict();
/** A bullet or numbered list. Each item is plain text rendered with the SAME
 *  safe inline subset as a paragraph (bold / italic / http links) — there is no
 *  html field and empty items are rejected, so no raw markup can pass through. */
export const ListBlockSchema = z.object({
    type: z.literal('list'),
    style: z.union([z.literal('bullet'), z.literal('numbered')]),
    items: z.array(text(1_000)).min(1).max(50),
}).strict();
/** A pull-quote: escaped text with an optional attribution line. Plain data. */
export const QuoteBlockSchema = z.object({
    type: z.literal('quote'),
    text: text(2_000),
    attribution: z.string().trim().max(200).optional(),
}).strict();
export const OfferBlockSchema = z.object({
    type: z.literal('offer'),
    heading: text(200),
    body: text(2_000),
    code: z.string().trim().max(60).optional(),
    buttonLabel: z.string().trim().max(60).optional(),
    buttonHref: httpUrl.optional(),
}).strict();
// ── Union + collection ──────────────────────────────────────────────────────
export const EmailBlockSchema = z.discriminatedUnion('type', [
    HeadingBlockSchema,
    ParagraphBlockSchema,
    ImageBlockSchema,
    ImageRowBlockSchema,
    ButtonBlockSchema,
    VideoPosterBlockSchema,
    DividerBlockSchema,
    OfferBlockSchema,
    ListBlockSchema,
    QuoteBlockSchema,
]);
/** An ordered list of blocks = one email body. 1..100 blocks. */
export const EmailBlocksSchema = z.array(EmailBlockSchema).min(1).max(100);
//# sourceMappingURL=emailBlock.js.map